import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useCutscene, cutsceneBridge } from '../../city/cutscene';
import { useHeliTour, heliBridge, padPose, padById, heliNow, stampHeliFrame } from '../../city/heliTour';
import { landIfDone } from '../../city/heliActions';
import { baseGroundY } from '../../city/cityGround';

// ABOARD A HELI TOUR (city/heliTour) — the camera, from the moment you board
// to the moment you are set down beside the pad. ONE, in the canvas (like
// ElevatorDirector), so nothing it holds goes with a remount.
//
//   BOARDED (on the pad, waiting for the first aboard to take off): the scene
//     takes the camera (cutsceneBridge: the walk and the chase camera stand
//     down, the HUD steps out, your body is hidden — you are inside), which
//     settles behind the helicopter, rotors ticking over.
//   FLYING: it follows the helicopter the server's clock says is in the air
//     (padPose on heliNow, the one flight clock) —
//       'chase'  behind and above it, steadied (it turns with the helicopter's
//                heading, not its bank or pitch: a horizon you can read by);
//       'cabin'  YOUR SEAT: inside, rolling with it (CABIN_SEATS — the model's
//                two rows of three, measured; the first aboard sits beside
//                the pilot, the rest fill in behind), looking ahead and, from a
//                window seat, out of your window.
//     The chase is SCRIPTED — no drag (Khari: "I assume this part is scripted
//     and the user shouldn't have control"); in your window seat you can drag
//     to look round. The city's sounds are heard from where the helicopter is
//     (cutsceneBridge.at).
//   DOWN: the pad is free again (the server's sweep, or a local flight's end):
//     you are stood on the apron beside your pad, facing the helicopter, and
//     the camera is yours.
const CHASE = { back: 10.5, up: 3.2, lookAhead: 5, lookUp: 0.6 };
const GROUND = { back: 9, up: 2.6, lookAhead: 1, lookUp: 0.9 };
// THE CABIN'S SEATS, in the helicopter's kit space (components/heli/Helicopter:
// nose +z, skids y=0, HELI_LENGTH 5.8 — these hold only at that length).
// Measured 2026-11-01 from the model's interior mesh (a side and a top scan):
// two rows of three, seat backs at z≈1.35 (the back row, the blue padding just
// behind it) and z≈1.92 (the front), cushions at y≈0.69, seats ≈0.33 apart
// across. The eye: over the cushion, a hand forward of the back. The pilot (an
// NPC) has the front right seat (kit −x is the helicopter's right); passengers
// take the rest in the order they boarded. Khari, 2026-11-01: "there are 2 rows
// of seats and a blue padding behind them. We are being placed behind the blue
// padding" — the first cut put the camera at z 0.85, behind the back row.
export const CABIN_SEATS = [
  { x: 0.33, z: 2.04, out: 0.6 },     // front left window, beside the pilot
  { x: 0.0, z: 2.04, out: 0 },        // front middle
  { x: 0.33, z: 1.47, out: 0.8 },     // back left window (turned to it: the seat ahead fills the rest)
  { x: -0.33, z: 1.47, out: -0.8 },   // back right window
  { x: 0.0, z: 1.47, out: 0 },        // back middle
];
/** The pilot's seat, front right (HeliPassengers seats the pilot there). */
export const PILOT_SEAT = { x: -0.33, z: 2.04, out: 0 };
const CABIN_EYE_Y = 1.0;
const CABIN_DOWN = -0.12;

const _e = new THREE.Euler(0, 0, 0, 'YXZ');
const _q = new THREE.Quaternion();
/**
 * A seat's view: the camera's position and turn for seat `i` in a helicopter at
 * `pose`, rolling and pitching with it, plus a drag look `d` { yaw, pitch }.
 */
export function cabinView(pose, i, d, outPos, outQuat) {
  const seat = CABIN_SEATS[Math.max(0, Math.min(CABIN_SEATS.length - 1, i | 0))];
  _e.set(-(pose.pitch || 0), pose.yaw || 0, pose.roll || 0, 'YXZ');
  outQuat.setFromEuler(_e);
  outPos.set(seat.x, CABIN_EYE_Y, seat.z).applyQuaternion(outQuat).add({ x: pose.x, y: pose.y, z: pose.z });
  // A camera looks down its own −z: a half turn faces it to the nose; +yaw turns it toward kit +x (the left).
  _e.set(CABIN_DOWN + (d?.pitch || 0), Math.PI + seat.out + (d?.yaw || 0), 0, 'YXZ');
  outQuat.multiply(_q.setFromEuler(_e));
}
const SETTLE_S = 1.3;
const STAND_OFF = 4.6;   // where you are set down: this far from the pad's centre, to the helicopter's side
const EYE = 0.75;

export default function HeliDirector() {
  const camera = useThree((s) => s.camera);
  const gl = useThree((s) => s.gl);
  const setEvents = useThree((s) => s.setEvents);

  // THE FLIGHT CLOCK, STAMPED BEFORE ANYTHING READS IT: a negative priority runs
  // ahead of every default useFrame (and, unlike a positive one, leaves r3f's own
  // render alone), so the camera here and the helicopter (Heliport) see one time.
  useFrame(() => stampHeliFrame(), -10);

  /* NOTHING IN THE CITY IS CLICKABLE FROM A HELICOPTER (Khari, 2026-11-02:
     "disable the user from pressing on location poles/markers while flying").
     A venue sign enters the venue; a resident opens a chat; neither belongs to
     someone strapped into a seat a hundred metres up. r3f's picking is switched
     off while you are aboard (boarding or flying) and back on when you step out
     — the seat view's drag is the canvas's own pointer events, not r3f's, so it
     keeps working. Nothing else toggles r3f events. */
  const picking = (on) => {
    setEvents({ enabled: on });
    if (!on) document.body.style.cursor = '';   // a hover left lit when picking stopped
  };
  useEffect(() => () => setEvents({ enabled: true }), [setEvents]);
  const run = useRef(null);   // { t0, fromPos, fromQuat, pad } while aboard
  const drag = useRef({ yaw: 0, pitch: 0, on: false, x: 0, y: 0 });
  const v = useMemo(() => ({
    pos: new THREE.Vector3(), look: new THREE.Vector3(), want: new THREE.Vector3(), aim: new THREE.Vector3(),
    q: new THREE.Quaternion(), e: new THREE.Euler(0, 0, 0, 'YXZ'), m: new THREE.Matrix4(), up: new THREE.Vector3(0, 1, 0),
    off: new THREE.Vector3(), at: new THREE.Vector3(), look2: new THREE.Quaternion(),
  }), []);

  // Drag to look round — from your seat only (the chase is scripted). The
  // walk's own drag stands down while a scene holds the camera (PlanetScene).
  useEffect(() => {
    const el = gl.domElement;
    const d = drag.current;
    const down = (e) => { if (!run.current || useHeliTour.getState().view !== 'cabin') return; d.on = true; d.x = e.clientX; d.y = e.clientY; };
    const move = (e) => {
      if (!d.on || !run.current) return;
      d.yaw -= (e.clientX - d.x) * 0.006; d.pitch = Math.max(-0.6, Math.min(0.5, d.pitch - (e.clientY - d.y) * 0.004));
      d.x = e.clientX; d.y = e.clientY;
    };
    const up = () => { d.on = false; };
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { el.removeEventListener('pointerdown', down); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [gl]);

  const release = (padId) => {
    run.current = null;
    picking(true);
    heliBridge.aboard = false; heliBridge.pose = null;
    const pad = padById(padId);
    if (pad) {
      // Beside the pad, on the helicopter's side, facing it.
      const sx = Math.cos(pad.rotY); const sz = -Math.sin(pad.rotY);   // the pad's own x axis
      const x = pad.x + sx * STAND_OFF; const z = pad.z + sz * STAND_OFF;
      camera.position.set(x, baseGroundY(x, z) + EYE, z);
      v.m.lookAt(camera.position, v.look.set(pad.x, camera.position.y - 0.1, pad.z), v.up);
      camera.quaternion.setFromRotationMatrix(v.m);
    }
    if (cutsceneBridge.scene?.kind === 'heli') { cutsceneBridge.active = false; cutsceneBridge.at = null; cutsceneBridge.scene = null; }
    const cs = useCutscene.getState();
    if (cs.phase === 'cut' && !cs.target) useCutscene.setState({ phase: 'off' });
    useHeliTour.setState({ caption: null });
  };

  useFrame((_, dt) => {
    landIfDone();
    const hu = useHeliTour.getState();
    const padId = hu.mine?.pad || null;
    const p = padId ? hu.pads[padId] : null;
    const aboard = !!p && (p.state === 'boarding' || p.state === 'flying')
      && (hu.offline || (p.seats || []).some((s) => s?.id && s.id === hu.myId));
    let r = run.current;
    if (!r && aboard) {
      if (cutsceneBridge.active && cutsceneBridge.scene?.kind !== 'heli') return;   // something else has the camera
      r = run.current = { t0: performance.now(), fromPos: camera.position.clone(), fromQuat: camera.quaternion.clone(), pad: padId };
      drag.current.yaw = 0; drag.current.pitch = 0;
      cutsceneBridge.active = true; cutsceneBridge.scene = { kind: 'heli' };
      useCutscene.setState({ phase: 'cut', target: null, caption: null });
      heliBridge.aboard = true;
      picking(false);
    }
    if (!r) return;
    if (!aboard) {
      // Seated in it no longer (it landed, or you got out before take-off).
      if (hu.mine && (!p || p.state === 'idle')) useHeliTour.setState({ mine: null });
      release(r.pad);
      return;
    }
    const pose = padPose(padId, heliNow(), hu);
    if (!pose) return;
    heliBridge.pose = pose;
    cutsceneBridge.at = v.at.set(pose.x, pose.y, pose.z);
    const d = drag.current;
    if (hu.view !== 'cabin' && (d.yaw || d.pitch)) { d.yaw = 0; d.pitch = 0; d.on = false; }   // back to the scripted chase
    const yaw = pose.yaw;
    if (hu.view === 'cabin' && p.state === 'flying') {
      // Inside, in your own seat (boarding order), rolling with it.
      const mySeat = hu.offline ? 0 : Math.max(0, (p.seats || []).findIndex((s) => s?.id === hu.myId));
      cabinView(pose, mySeat, d, v.want, v.q);
      camera.position.copy(v.want);
      const e = Math.min(1, (performance.now() - r.t0) / 1000 / SETTLE_S);
      if (e < 1) camera.quaternion.slerp(v.q, Math.min(1, dt * 6)); else camera.quaternion.copy(v.q);
      return;
    }
    // Behind and above, turned with the heading (not the bank).
    const c = p.state === 'flying' && pose.phase !== 'ground' ? CHASE : GROUND;
    const fx = Math.sin(yaw); const fz = Math.cos(yaw);
    v.want.set(pose.x - fx * c.back, pose.y + c.up, pose.z - fz * c.back);
    v.aim.set(pose.x + Math.sin(pose.yaw) * c.lookAhead, pose.y + c.lookUp, pose.z + Math.cos(pose.yaw) * c.lookAhead);
    const e = Math.min(1, (performance.now() - r.t0) / 1000 / SETTLE_S);
    if (e < 1) {
      const k = e * e * (3 - 2 * e);
      camera.position.lerpVectors(r.fromPos, v.want, k);
    } else {
      camera.position.lerp(v.want, 1 - Math.exp(-5 * dt));
    }
    v.m.lookAt(camera.position, v.aim, v.up);
    v.q.setFromRotationMatrix(v.m);
    if (e < 1) camera.quaternion.slerpQuaternions(r.fromQuat, v.q, e * e * (3 - 2 * e));
    else camera.quaternion.slerp(v.q, 1 - Math.exp(-8 * dt));
  });

  return null;
}
